import React, { useState, useEffect } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  GraduationCap,
  Users,
  Target,
  CheckCircle,
  CreditCard,
  Building2,
  Loader2,
  Shield,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const ApplyOnline = () => {
  const [formData, setFormData] = useState({
    course: "",
    courseName: "",
    programType: "",
    name: "",
    fatherName: "",
    motherName: "",
    dateOfBirth: "",
    gender: "",
    lastAcademicQualification: "",
    yearOfPassing: "",
    residentialPhone: "",
    mobileNumber: "",
    email: "",
    fullAddress: "",
    centreBranchName: "",
    centreCityName: "",
    heardAboutINSD: "",
    goalForCourse: "",
    parentGuardianName: "",
    parentPhoneNumber: "",
    parentAddress: "",
    parentOccupation: "",
    parentEmail: "",
    parentSupport: "",
    recaptchaVerified: false,
  });
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [bankAccount, setBankAccount] = useState({});
  const [selectedCourseDetails, setSelectedCourseDetails] = useState(null);

  const bankAccountDetails = () => {
    let data = "";

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/bank/get`,
      headers: {},
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        console.log(JSON.stringify(response.data));
        setBankAccount(response?.data?.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    bankAccountDetails();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/courses/get-all`
        );
        const data = await response.json();
        if (data.success) {
          setCourses(data.data);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "course") {
      const selectedCourse = courses.find((course) => course._id === value);
      setSelectedCourseDetails(selectedCourse);

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        courseName: selectedCourse
          ? `${selectedCourse.category} - ${selectedCourse.name}`
          : "",
        programType: "", // Reset program type when course changes
      }));
    } else if (name === "programType") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getApplicationFee = () => {
    if (!selectedCourseDetails || !formData.programType) {
      return 1500; // Default fee
    }

    const selectedProgram = selectedCourseDetails.programs.find(
      (program) => program.programType === formData.programType
    );

    return selectedProgram ? selectedProgram.onlineApplicationFee : 1500;
  };

  const applicationFee = getApplicationFee();

  const handleRecaptcha = () => {
    if (formData.recaptchaVerified || captchaLoading) return;

    setCaptchaLoading(true);
    setTimeout(() => {
      setCaptchaLoading(false);
      setFormData({ ...formData, recaptchaVerified: true });
    }, 1500);
  };

  const resetCaptcha = () => {
    setFormData({ ...formData, recaptchaVerified: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.recaptchaVerified) {
      alert("Please verify that you are not a robot");
      return;
    }

    setLoading(true);

    try {
      // Create the submission data with applicationFee
      const submissionData = {
        ...formData,
        applicationFee: applicationFee, // Add the calculated application fee
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/application/apply-online`, // Fixed URL (removed ./)
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Success:", data);
        setSubmitted(true);
      } else {
        const error = await response.json();
        alert(`Submission failed: ${error.message || "Please try again."}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateStep = (step) => {
    if (step === 1) {
      return (
        formData.name &&
        formData.dateOfBirth &&
        formData.gender &&
        formData.email &&
        formData.mobileNumber
      );
    }
    if (step === 2) {
      return (
        formData.lastAcademicQualification &&
        formData.yearOfPassing &&
        formData.course
      );
    }
    if (step === 3) {
      return (
        formData.parentGuardianName &&
        formData.parentPhoneNumber &&
        formData.parentEmail
      );
    }
    return true;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#DA3335]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-[#DA3335]" />
          </div>
          <h2 className="text-3xl font-bold mb-3 text-[#1b0a0a]">
            Application Submitted!
          </h2>
          <p className="mb-8 text-[#3c3737]">
            Thank you for applying to INSD. We'll review your application and
            get back to you soon.
          </p>
          <button
            onClick={() => navigate("/apply-online")}
            className="px-6 py-3 bg-[#1b0a0a] text-white rounded-lg font-medium transition-all hover:opacity-90"
          >
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 text-[#1b0a0a] heading">
            Apply Online
          </h1>
          <p className="text-[#3c3737] sub-heading">
            Begin your journey with INSD - Fill in your details below
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8 max-w-xl md:max-w-3xl  mx-auto">
          {[
            { num: 1, label: "Personal Info" },
            { num: 2, label: "Academic" },
            { num: 3, label: "Guardian Info" },
            { num: 4, label: "Payment & Verify" },
          ].map((step, idx) => (
            <div
              key={step.num}
              className="flex items-center flex-1 sub-heading"
            >
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    currentStep >= step.num
                      ? "bg-[#144982] text-white"
                      : "bg-gray-200 text-[#3c3737]"
                  }`}
                >
                  {step.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${
                    currentStep >= step.num
                      ? "text-[#144982]"
                      : "text-[#3c3737]"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-all ${
                    currentStep > step.num ? "bg-[#144982]" : "bg-gray-300"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-lg p-8"
        >
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-[#DA3335]">
                <div className="p-2 rounded-lg bg-[#DA3335]/20">
                  <User className="w-6 h-6 text-[#DA3335]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1b0a0a]">
                  Personal Information
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Father's Name *
                  </label>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Father's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Mother's Name *
                  </label>
                  <input
                    type="text"
                    name="motherName"
                    value={formData.motherName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Mother's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Gender *
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                  >
                    <option value="">Select Gender</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        setFormData({ ...formData, mobileNumber: value });
                      }
                    }}
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="10-digit mobile number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Residential Phone
                  </label>
                  <input
                    type="tel"
                    name="residentialPhone"
                    value={formData.residentialPhone}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        setFormData({ ...formData, residentialPhone: value });
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Optional"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Full Address *
                  </label>
                  <textarea
                    name="fullAddress"
                    value={formData.fullAddress}
                    onChange={handleChange}
                    required
                    rows="3"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all resize-none text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Enter your complete address"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Academic Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-[#DA3335]">
                <div className="p-2 rounded-lg bg-[#144982]/20">
                  <GraduationCap className="w-6 h-6 text-[#144982]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1b0a0a]">
                  Academic & Course Details
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Course Applying For *
                  </label>
                  <select
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    required
                    className="text-ellipsis overflow-hidden whitespace-nowrap w-full max-w-sm px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                  >
                    <option value="" className="">
                      Select a course
                    </option>
                    {courses.map((course) => (
                      <option key={course._id} value={course._id}>
                        {course.category} - {course.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Program Type Selection */}
                {selectedCourseDetails && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                      Program Type *
                    </label>
                    <select
                      name="programType"
                      value={formData.programType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    >
                      <option value="">Select Program Type</option>
                      {selectedCourseDetails.programs.map((program) => (
                        <option key={program._id} value={program.programType}>
                          {program.programType} - {program.duration} (Fee: ₹
                          {program.onlineApplicationFee})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Last Academic Qualification *
                  </label>
                  <input
                    type="text"
                    name="lastAcademicQualification"
                    value={formData.lastAcademicQualification}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="e.g., B.Sc in Computer Science"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Year of Passing *
                  </label>
                  <input
                    type="number"
                    name="yearOfPassing"
                    value={formData.yearOfPassing}
                    onChange={handleChange}
                    required
                    min="1950"
                    max="2025"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="YYYY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Centre Branch Name *
                  </label>
                  <input
                    type="text"
                    name="centreBranchName"
                    value={formData.centreBranchName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Branch name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Centre City Name *
                  </label>
                  <input
                    type="text"
                    name="centreCityName"
                    value={formData.centreCityName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="City name"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    How did you hear about INSD? *
                  </label>
                  <select
                    name="heardAboutINSD"
                    value={formData.heardAboutINSD}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                  >
                    <option value="">Select an option</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Friend/Family">Friend/Family</option>
                    <option value="Website">Website</option>
                    <option value="Advertisement">Advertisement</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Your Goal for this Course *
                  </label>
                  <textarea
                    name="goalForCourse"
                    value={formData.goalForCourse}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all resize-none text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Tell us about your career goals and aspirations..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Parent/Guardian Information */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-[#DA3335]">
                <div className="p-2 rounded-lg bg-[#DA3335]/20">
                  <Users className="w-6 h-6 text-[#DA3335]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1b0a0a]">
                  Parent/Guardian Details
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Parent/Guardian Name *
                  </label>
                  <input
                    type="text"
                    name="parentGuardianName"
                    value={formData.parentGuardianName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Parent Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="parentPhoneNumber"
                    value={formData.parentPhoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        setFormData({ ...formData, parentPhoneNumber: value });
                      }
                    }}
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="10-digit number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Parent Email *
                  </label>
                  <input
                    type="email"
                    name="parentEmail"
                    value={formData.parentEmail}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="parent@email.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Parent Address *
                  </label>
                  <textarea
                    name="parentAddress"
                    value={formData.parentAddress}
                    onChange={handleChange}
                    required
                    rows="3"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all resize-none text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Complete address"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Parent Occupation *
                  </label>
                  <input
                    type="text"
                    name="parentOccupation"
                    value={formData.parentOccupation}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="e.g., Business, Service, etc."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-[#1b0a0a]">
                    Do your Parents / Spouse support and encourage your interest
                    in this field? *
                  </label>
                  <textarea
                    name="parentSupport"
                    value={formData.parentSupport}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none transition-all resize-none text-[#1b0a0a] focus:border-[#144982]"
                    placeholder="Please describe how your parents/spouse support your interest in this field..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Payment & Verification */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-[#DA3335]">
                <div className="p-2 rounded-lg bg-[#144982]/20">
                  <CreditCard className="w-6 h-6 text-[#144982]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1b0a0a]">
                  Payment & Verification
                </h2>
              </div>

              {/* Payment Information */}
              <div className="border-2 border-[#144982] rounded-lg p-6 bg-[#144982]/5">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="w-6 h-6 text-[#144982]" />
                  <h3 className="text-xl font-bold text-[#1b0a0a]">
                    Application Fee: ₹{applicationFee}
                  </h3>
                </div>
                <p className="text-sm mb-4 text-[#3c3737]">
                  Please transfer the application fee to the following account:
                </p>

                <div className="bg-white rounded-lg p-5 space-y-4 shadow-sm">
                  <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                    <Building2 className="w-5 h-5 text-[#144982]" />
                    <div>
                      <p className="text-xs font-medium text-[#3c3737]">
                        Account Holder
                      </p>
                      <p className="text-lg font-bold text-[#1b0a0a]">
                        {bankAccount.accountHolder}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-1 text-[#3c3737]">
                        Account number
                      </p>
                      <p className="text-base font-bold text-[#144982]">
                        {bankAccount.accountNumber}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-1 text-[#3c3737]">
                        IFSC Code
                      </p>
                      <p className="text-base font-bold text-[#144982]">
                        {bankAccount.ifsc}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-1 text-[#3c3737]">
                        Bank Name
                      </p>
                      <p className="text-base font-semibold text-[#1b0a0a]">
                        {bankAccount.bankName}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-1 text-[#3c3737]">
                        Branch
                      </p>
                      <p className="text-base font-semibold text-[#1b0a0a]">
                        {bankAccount.branch}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <span className="font-bold">Note:</span> After making the
                    payment, please proceed with the form submission. Our team
                    will verify your payment details.
                  </p>
                </div>
              </div>

              {/* Enhanced reCAPTCHA */}
              <div className="mt-6">
                <div
                  className={`relative border-2 rounded-lg p-5 transition-all duration-300 ${
                    formData.recaptchaVerified
                      ? "bg-green-50 border-green-500"
                      : "bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    {/* Checkbox Button */}
                    <button
                      type="button"
                      onClick={handleRecaptcha}
                      disabled={formData.recaptchaVerified}
                      className={`flex-shrink-0 w-8 h-8 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        formData.recaptchaVerified
                          ? "bg-green-500 border-green-500"
                          : "bg-white border-gray-400 hover:border-[#144982]"
                      } ${captchaLoading ? "cursor-wait" : "cursor-pointer"}`}
                    >
                      {captchaLoading && (
                        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                      )}
                      {!captchaLoading && formData.recaptchaVerified && (
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>

                    {/* Text Content */}
                    <div className="flex">
                      <p className="text-base font-medium text-[#1b0a0a] flex items-center gap-2">
                        I'm not a robot
                        {formData.recaptchaVerified && (
                          <span className="text-sm text-green-600 font-semibold">
                            ✓ Verified
                          </span>
                        )}
                      </p>
                    </div>

                    {/* reCAPTCHA Branding & Reset */}
                    <div className="flex flex-col md:flex-row  items-center gap-3">
                      {formData.recaptchaVerified && (
                        <button
                          type="button"
                          onClick={resetCaptcha}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#144982] hover:bg-[#144982]/10 rounded transition-all"
                          title="Reset verification"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Reset
                        </button>
                      )}

                      <div className="text-right md:border-l border-gray-300 pl-3 ">
                        <div className="flex items-center gap-2 text-xs font-medium mb-1 text-[#3c3737]">
                          <Shield className="w-4 h-4 text-[#144982]" />
                          <span>reCAPTCHA</span>
                        </div>
                        <div className="flex gap-2 text-xs text-[#3c3737]">
                          <span className="hover:underline cursor-pointer">
                            Privacy
                          </span>
                          <span>-</span>
                          <span className="hover:underline cursor-pointer">
                            Terms
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Verification Progress Bar */}
                  {captchaLoading && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-[#144982] h-1.5 rounded-full animate-pulse"
                          style={{ width: "60%" }}
                        ></div>
                      </div>
                      <p className="text-xs text-[#3c3737] mt-1">
                        Verifying security check...
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Apply Online Image */}
              <div className="mt-6">
                <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                  <img
                    src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80"
                    alt="Apply Online"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4 text-center">
                    <h3 className="text-lg font-bold mb-1 text-[#1b0a0a]">
                      Start Your Journey Today!
                    </h3>
                    <p className="text-sm text-[#3c3737]">
                      Join thousands of students who have transformed their
                      careers with INSD
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t-2 border-gray-200">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-6 py-2.5 bg-gray-200 text-[#1b0a0a] rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Previous
              </button>
            )}

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={nextStep}
                className="ml-auto px-6 py-2.5 bg-[#144982] text-white rounded-lg font-semibold hover:opacity-90 transition-all"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || !formData.recaptchaVerified}
                className="ml-auto px-6 py-2.5 bg-[#1b0a0a] text-white rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Submit Application"
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Apply Online Image Section */}
      <div className="mt-12 text-center">
        <div className="w-full mx-auto bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
          <img
            src="https://static.vecteezy.com/system/resources/previews/002/294/878/non_2x/learning-and-education-web-banner-design-free-vector.jpg"
            alt="Apply Online"
            className="w-full h-64 object-cover"
          />
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Start Your Journey Today!
            </h3>
            <p className="text-gray-600 text-sm">
              Join thousands of students who have transformed their careers with
              INSD
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyOnline;
